import { createRouter, createWebHistory } from 'vue-router'
import type { RouteRecordRaw } from 'vue-router'
import { authGuard } from './guards/auth.guard'
import { setupRouteGuards } from './guards'

const routes: RouteRecordRaw[] = [
  {
    path: '/',
    redirect: '/login'
  },
  {
    path: '/reports',
    name: 'Reports',
    component: () => import('@/views/ReportBuilder.vue'),
    meta: { requiresAuth: true, permissions: ['reports:read'] }
  },
  {
    path: '/login',
    name: 'Login',
    component: () => import('@/views/Login.vue'),
    meta: { requiresAuth: false }
  },
  {
    path: '/forgot-password',
    name: 'ForgotPassword',
    component: () => import('@/views/auth/ForgotPassword.vue'),
    meta: { requiresAuth: false }
  },
  {
    path: '/reset-password/:token',
    name: 'ResetPassword',
    component: () => import('@/views/auth/ResetPassword.vue'),
    meta: { requiresAuth: false }
  },
  {
    path: '/access-denied',
    name: 'AccessDenied',
    component: () => import('@/views/AccessDenied.vue'),
    meta: { requiresAuth: true }
  },
  {
    path: '/permissions',
    name: 'CurrentUserPermissions',
    component: () => import('@/components/CurrentUserPermissions.vue'),
    meta: { requiresAuth: true }
  },
  {
    path: '/menu-diagnostic',
    name: 'MenuDiagnostic',
    component: () => import('@/components/MenuDiagnostic.vue'),
    meta: { requiresAuth: true }
  },
  {
    path: '/dashboard',
    name: 'Dashboard',
    component: () => import('@/views/Dashboard.vue'),
    meta: { requiresAuth: true }
  },
  {
    path: '/employes',
    name: 'Employes',
    component: () => import('@/views/Employes.vue'),
    meta: { requiresAuth: true }
  },
  {
    path: '/paie',
    name: 'Paie',
    component: () => import('@/views/Paie.vue'),
    meta: { requiresAuth: true }
  },
  {
    path: '/paie/composants',
    name: 'ComposantsPaie',
    component: () => import('@/views/ComposantsPaie.vue'),
    meta: { requiresAuth: true }
  },
  {
    path: '/paie/simulateur',
    name: 'SimulateurBulletin',
    component: () => import('@/views/SimulateurBulletin.vue'),
    meta: { requiresAuth: true }
  },
  {
    path: '/absences',
    name: 'Absences',
    component: () => import('@/views/Absences.vue'),
    meta: { requiresAuth: true }
  },
  {
    path: '/presence',
    name: 'Presence',
    component: () => import('@/views/Presence.vue'),
    meta: { requiresAuth: true }
  },
  {
    path: '/pointage',
    name: 'Pointage',
    component: () => import('@/views/Pointage.vue'),
    meta: { requiresAuth: true, title: 'Pointage' }
  },
  {
    path: '/remuneration',
    name: 'Remuneration',
    component: () => import('@/views/Remuneration.vue'),
    meta: { requiresAuth: true }
  },
  {
    path: '/notes-frais',
    name: 'NotesFrais',
    component: () => import('@/views/NotesFrais.vue'),
    meta: { requiresAuth: true }
  },
  {
    path: '/syndicats',
    name: 'Syndicats',
    component: () => import('@/views/Syndicats.vue'),
    meta: { requiresAuth: true }
  },
  {
    path: '/equipements',
    name: 'Equipements',
    component: () => import('@/views/Equipements.vue'),
    meta: { requiresAuth: true }
  },
  {
    path: '/documents',
    name: 'Documents',
    component: () => import('@/views/Documents.vue'),
    meta: { requiresAuth: true }
  },
  {
    path: '/configuration',
    name: 'Configuration',
    component: () => import('@/views/Configuration.vue'),
    meta: { requiresAuth: true }
  },
  {
    path: '/utilisateurs',
    name: 'Utilisateurs',
    component: () => import('@/views/Utilisateurs.vue'),
    meta: { requiresAuth: true }
  },
  {
    path: '/employes/:id',
    name: 'EmployeDetails',
    component: () => import('@/views/EmployeDetails.vue'),
    meta: { requiresAuth: true }
  },
  {
    path: '/cycles-paie/:id',
    name: 'CyclePaieDetails',
    component: () => import('@/views/CyclePaieDetails.vue'),
    meta: { requiresAuth: true }
  },
  // Routes pour le module Dashboards
  {
    path: '/dashboards',
    name: 'DashboardsManagement',
    component: () => import('@/views/DashboardsManagementView.vue'),
    meta: { requiresAuth: true }
  },
  {
    path: '/dashboards/editor',
    name: 'DashboardEditor',
    component: () => import('@/views/DashboardEditorManagementView.vue'),
    meta: { requiresAuth: true }
  },
  {
    path: '/dashboards/editor/:id',
    name: 'DashboardEditorEdit',
    component: () => import('@/views/DashboardEditorManagementView.vue'),
    meta: { requiresAuth: true }
  },
  {
    path: '/dashboards/view/:id',
    name: 'DashboardViewer',
    component: () => import('@/views/DashboardViewerManagementView.vue'),
    meta: { requiresAuth: true }
  },
  {
    path: '/menu-visibility-test',
    name: 'MenuVisibilityTest',
    component: () => import('@/components/MenuVisibilityTest.vue'),
    meta: { title: 'Test de Visibilité des Menus' }
  },
  {
    path: '/permission-debugger',
    name: 'PermissionDebugger',
    component: () => import('@/components/PermissionDebugger.vue'),
    meta: { title: 'Debug des Permissions' }
  },
  {
    path: '/notifications',
    name: 'Notifications',
    component: () => import('@/views/Notifications.vue'),
    meta: { requiresAuth: true, title: 'Notifications' }
  }
]

const router = createRouter({
  history: createWebHistory(),
  routes,
  scrollBehavior(to, from, savedPosition) {
    // Si on a une position sauvegardée (retour arrière), l'utiliser
    if (savedPosition) {
      return savedPosition
    }
    
    // Si on navigue vers une nouvelle page, aller en haut
    if (to.path !== from.path) {
      return { top: 0, left: 0 }
    }
    
    // Sinon, rester à la position actuelle
    return false
  }
})

// Navigation guard pour l'authentification
router.beforeEach(authGuard)

// Navigation guard pour le scroll vers le haut
router.afterEach((to, from) => {
  // Scroll vers le haut seulement si on change de route
  if (to.path !== from.path) {
    // Essayer plusieurs méthodes pour s'assurer que le scroll fonctionne
    setTimeout(() => {
      // Méthode 1: window.scrollTo
      window.scrollTo(0, 0)
      
      // Méthode 2: document.documentElement.scrollTop
      document.documentElement.scrollTop = 0
      document.body.scrollTop = 0
      
      // Méthode 3: scrollIntoView sur le body
      document.body.scrollIntoView({ behavior: 'smooth', block: 'start' })
    }, 100)
  }
})

// Configuration des gardes de permissions
setupRouteGuards(router)

export default router
